import type { IAgentToolContext, IAgentTool } from '../../../../core/ai/agent/IAgentTool';

export const queryTool: IAgentTool = {
  name: 'flow_query',
  description: 'Analyze flowchart paths, dead ends, and loops.',
  parameters: {
    type: 'object',
    properties: {
      query: { type: 'string', description: 'Question about the flow (e.g., dead ends, loops)' },
    },
    required: ['query'],
  },
  supportedDiagramTypes: ['flowchart'],
  tier: 'safe',
  category: 'query',

  async execute(params: Record<string, unknown>, context: IAgentToolContext) {
    const query = String(params.query ?? '').trim().toLowerCase();
    const nodes = context.diagramData.nodes;
    const edges = context.diagramData.edges;

    const outgoing = new Map<string, number>();
    const incoming = new Map<string, number>();
    edges.forEach((edge) => {
      outgoing.set(edge.from, (outgoing.get(edge.from) ?? 0) + 1);
      incoming.set(edge.to, (incoming.get(edge.to) ?? 0) + 1);
    });

    if (query.includes('dead')) {
      const deadEnds = nodes.filter((n) => (outgoing.get(n.id) ?? 0) === 0 && (incoming.get(n.id) ?? 0) > 0);
      return {
        success: true,
        summary: deadEnds.length === 0 ? 'No dead ends found.' : `Found ${deadEnds.length} dead end(s).`,
        data: { nodeIds: deadEnds.map((n) => n.id) },
      };
    }

    if (query.includes('loop') || query.includes('cycle')) {
      const visited = new Set<string>();
      const stack = new Set<string>();
      let hasCycle = false;

      const adjacency = new Map<string, string[]>();
      edges.forEach((edge) => {
        const list = adjacency.get(edge.from) ?? [];
        list.push(edge.to);
        adjacency.set(edge.from, list);
      });

      const dfs = (nodeId: string) => {
        if (stack.has(nodeId)) {
          hasCycle = true;
          return;
        }
        if (visited.has(nodeId) || hasCycle) return;
        visited.add(nodeId);
        stack.add(nodeId);
        (adjacency.get(nodeId) ?? []).forEach((next) => dfs(next));
        stack.delete(nodeId);
      };

      nodes.forEach((node) => dfs(node.id));
      return {
        success: true,
        summary: hasCycle ? 'Detected at least one loop in the flow.' : 'No loops detected.',
      };
    }

    return {
      success: true,
      summary: `Flowchart has ${nodes.length} nodes and ${edges.length} connectors.`,
    };
  },
};
